import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2, NgZone, AfterViewInit, OnChanges } from '@angular/core';
import { RouterLinkActive, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { AdminAuthService } from 'src/app/shared/services/auth.service';

@Component({
    selector: 'navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    imports: [CommonModule, MatToolbarModule, MatIconModule, MatRippleModule, MatButtonModule, RouterModule, NgxTippyModule]
})
export class NavigationComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() isExpanded: boolean = true;
  @Input() isMobileScreen?: boolean;
  @Input() isMobileNavOpen?: boolean;
  @Input() activeSecondaryNavUrl: string = '';
  @Output() toggleDesktopNav = new EventEmitter();
  @Output() toggleMobileNav = new EventEmitter();
  @Output() closeSettingsNav = new EventEmitter();
  @Output() closeTaaConfigNav = new EventEmitter();

  private _unsubscribe$: Subject<void> = new Subject<void>();
  public subNavExpanded: boolean = false;
  public showNavArrow!: boolean;
  public timeClockSubRoute: string = 'my-time';

  public hasDirectReports: boolean = false;

  @ViewChild('mainNav', { static: true }) mainNavElement: ElementRef = new ElementRef(HTMLElement);

  public unlistenClickOut!: () => void;
  public unlistenTrapFocus!: () => void;

  constructor(
    private _renderer: Renderer2,
    private _ngZone: NgZone,
    private _authService: AdminAuthService
  ) {
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setActiveClass();
    });
  }

  ngOnChanges(): void {
    if (!this.isExpanded) {
      let subNavs = this.mainNavElement.nativeElement.querySelectorAll('.sub-nav-menu');

      if (subNavs.length > 0) {
        for (let i = 0; i < subNavs.length; i++) {
          if (!subNavs[i].classList.contains('show')) {
            let expandIcon = subNavs[i]?.nextElementSibling.children[2];
            subNavs[i].classList.toggle('show');
            this._renderer.setAttribute(subNavs[i]?.nextElementSibling, 'aria-expanded', 'false');
            this._renderer.removeClass(expandIcon, 'rotate-icon');
            if (subNavs[i]?.parentElement?.classList.contains('active')) {
              this._renderer.addClass(subNavs[i]?.nextElementSibling, 'active');
            }
          }
        }
      }
    }
  }

  /**When on mobile screen and mobile nav is open, add
   * listen on click to detect whether the user clicks inside the nav or
   * out. Once the user clicks out, the nav closes, and the listener will be destroyed in header component.
   * This will also be destroyed onResize if user resizes screen to desktop view in header component.
   * */
  setUpClickOutsideListener(): void {
    const navEl = this.mainNavElement.nativeElement;
    this._ngZone.runOutsideAngular(() => {
      this.unlistenClickOut = this._renderer.listen('document', 'click', (event: MouseEvent | TouchEvent) => {
        const clickedInside = navEl.contains(event.target);
        if (!clickedInside) {
          this.toggleNav(event);
        }
      });
    });
  }

  /**
   * When the mobile nav overlays -
   *force the keyboard event to only focus on elements within the navigation menu
   * */
  public setUpTrapFocus(): void {
    const focusableEls = this.mainNavElement.nativeElement.querySelectorAll('a[href]:not([disabled]), button:not([disabled])');
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    this.unlistenTrapFocus = this._renderer.listen(this.mainNavElement.nativeElement, 'keydown', (e: KeyboardEvent) => {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) {
        return;
      }

      if (e.shiftKey) {
        /* shift + tab */ if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    });
  }

  /**
   * If on mobile screen, and nav link has been clicked, call toggleNav method to close the main nav overlay.
   * Also check if the clicked link is already active. On mobile, the user will be able
   * to open the Settings Nav like an overlay. So if the link is already active, and won't trigger OnActivate
   * method in app component - then the Settings Nav will remain open over the page. This routerLinkActive check
   * will emit to app to call the settingsNavToggle method to close.
   * @param routerLink
   * @param event
   */
  public navLinkClickedOnMobile(routerLink: RouterLinkActive, event: MouseEvent | KeyboardEvent | TouchEvent): void {
    this.toggleNav(event);
    if (routerLink.isActive) {
      this.closeSettingsNav.emit();
      this.closeTaaConfigNav.emit();
    }
  }

  /**
   * expand/collapse nav on desktop - open/close nav on mobile
   * @param event
   */
  public toggleNav(event: MouseEvent | KeyboardEvent | TouchEvent): void {
    this._ngZone.run(() => {
      !this.isMobileScreen ? this.toggleDesktopNav.emit(event) : this.toggleMobileNav.emit(event);
    });
  }

  /**
   * Toggle sub nav on click and toggle active class
   * @param event
   */
  public openSubNav(event: MouseEvent | KeyboardEvent | TouchEvent): void {
    const targetElement = event.currentTarget as HTMLElement;

    targetElement?.nextElementSibling?.classList.toggle('show');
    if (targetElement?.nextElementSibling?.classList.contains('show')) {
      if (targetElement.classList.contains('active')) {
        this._renderer.removeClass(targetElement, 'active');
      }
      this._renderer.addClass(targetElement.children[2], 'rotate-icon');
      this._renderer.setAttribute(targetElement, 'aria-expanded', 'true');
    } else {
      if (targetElement?.parentElement?.classList.contains('active')) {
        this._renderer.addClass(targetElement, 'active');
      }
      this._renderer.removeClass(targetElement.children[2], 'rotate-icon');
      this._renderer.setAttribute(targetElement, 'aria-expanded', 'false');
    }

    if (!this.isExpanded) {
      this.toggleNav(event);
    }
  }

  /**
   * if route is active under the sub menus,
   * add active class to the parent on load
   * */
  public setActiveClass(): void {
    const activeLink = this.mainNavElement.nativeElement?.querySelector('.nav-list-item.active');
    if (activeLink != null) {
      this._renderer.addClass(activeLink.querySelector('.sub-nav-btn'), 'active');
    }
  }

  get showCustomerService(): boolean {
    return this._authService.hasUserRole('customerservice');
  }

  get showCMS(): boolean {
    return this._authService.hasUserRole('cms');
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.unsubscribe();
  }
}
